export async function* readJsonSse<T>(
  stream: ReadableStream<Uint8Array>,
  options: { requireTerminal?: boolean } = {}
): AsyncGenerator<T, void, void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let terminalEventReceived = false;

  const parseFrame = (frame: string): T | undefined => {
    const data = frame
      .split(/\r?\n/)
      .filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).trimStart())
      .join('\n')
      .trim();

    if (!data || data === '[DONE]') return undefined;
    return JSON.parse(data) as T;
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (value) buffer += decoder.decode(value, { stream: !done });
      if (done) buffer += decoder.decode();

      let boundary = buffer.match(/\r?\n\r?\n/);
      while (boundary?.index !== undefined) {
        const frame = buffer.slice(0, boundary.index);
        buffer = buffer.slice(boundary.index + boundary[0].length);
        const event = parseFrame(frame);
        if (event !== undefined) {
          terminalEventReceived ||= isTerminalEvent(event);
          yield event;
        }
        boundary = buffer.match(/\r?\n\r?\n/);
      }

      if (done) {
        const event = parseFrame(buffer);
        if (event !== undefined) {
          terminalEventReceived ||= isTerminalEvent(event);
          yield event;
        }
        break;
      }
    }
    if (options.requireTerminal && !terminalEventReceived) {
      throw new Error('SSE stream ended before a terminal event');
    }
  } finally {
    reader.releaseLock();
  }
}

function isTerminalEvent(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const event = value as { type?: unknown; done?: unknown };
  return (
    event.type === 'done' ||
    event.type === 'revert' ||
    event.type === 'error' ||
    event.done === true
  );
}
